// src/components/Calculator.js
import React, { useState } from "react";
import {
  Package,
  Edit3,
  Clock,
  Trash2,
  AlertCircle,
  Plus,
  RefreshCw,
  XCircle,
  Copy,
  Search,
  TrendingDown,
  Info,
  Lock,
  List,
} from "lucide-react";
import {
  CUSHION_TYPES,
  CUSHION_DENSITIES,
  CUSHION_OPTIONS,
  SOURCE_OPTIONS,
  LOGO_PROCESSING_OPTIONS,
  APRON_CATEGORIES,
  SOFA_AREA_TYPES,
} from "../data";
import getItemDetails from "../utils/getItemDetails";

const Calculator = ({
  theme,
  inputs,
  setInputs,
  items,
  setItems,
  productType,
  editingHistoryId,
  isManualSource,
  setIsManualSource,
  handleItemChange,
  addItemRow,
  clearForm,
  handleCancelEdit,
  handleCopyAndSave,
  validateForm,
  handleCopyItemizedPrices,
  generateQuotationText,
  calculationResult,
  relatedRecords,
  loadHistoryRecord,
  sofaStyles,
  carpetStyles,
  edgingCarpetStyles,
  wallStickerStyles,
  cushionThicknesses,
  apronStyles,
  userRole,
  showToast,
}) => {
  const [apronSearch, setApronSearch] = useState({});

  // 處理圍裙 LOGO 複選變更
  const handleLogoChange = (itemId, logoValue, checked) => {
    const item = items.find((i) => i.id === itemId);
    const currentLogos = item.selectedLogos || {};
    const newLogos = { ...currentLogos };

    if (checked) {
      newLogos[logoValue] = 1; // 預設 1 個
    } else {
      newLogos[logoValue] = 0;
    }

    // 計算有多少種 LOGO 被選中
    let activeTypes = 0;
    Object.values(newLogos).forEach((qty) => {
      if (qty > 0) activeTypes++;
    });

    setItems((prev) =>
      prev.map((it) => {
        if (it.id === itemId) {
          return {
            ...it,
            selectedLogos: newLogos,
            plateCount: activeTypes, // 自動更新版費數量
          };
        }
        return it;
      })
    );
  };

  // 處理 LOGO 數量變更
  const handleLogoCountChange = (itemId, logoValue, count) => {
    const item = items.find((i) => i.id === itemId);
    const currentLogos = item.selectedLogos || {};
    const newLogos = { ...currentLogos, [logoValue]: parseInt(count) || 0 };
    handleItemChange(itemId, "selectedLogos", newLogos);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-6">
        <div
          className={`p-6 rounded-2xl shadow-sm border space-y-5 transition-colors ${
            editingHistoryId
              ? "bg-amber-50 border-amber-200"
              : "bg-white border-slate-100"
          }`}
        >
          <div className="flex justify-between items-center">
            <h2
              className={`text-lg font-semibold flex items-center gap-2 ${
                editingHistoryId ? "text-amber-700" : "text-slate-700"
              }`}
            >
              {editingHistoryId ? <Edit3 size={20} /> : <Package size={20} />}{" "}
              {editingHistoryId ? "編輯訂單" : "訂單資訊"}
            </h2>
            <div className="flex items-center gap-1.5 text-sm text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full font-mono">
              <Clock size={14} /> {inputs.date}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">來源</label>
              <select
                value={isManualSource ? "其他" : inputs.source}
                onChange={(e) => {
                  if (e.target.value === "其他") {
                    setIsManualSource(true);
                    setInputs((p) => ({ ...p, source: "" }));
                  } else {
                    setIsManualSource(false);
                    setInputs((p) => ({ ...p, source: e.target.value }));
                  }
                }}
                className="w-full p-2.5 bg-white/50 border rounded-lg outline-none border-slate-200"
              >
                <option value="" disabled>
                  請選擇
                </option>
                {SOURCE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
                <option value="其他">其他</option>
              </select>
              {isManualSource && (
                <input
                  type="text"
                  placeholder="輸入來源"
                  value={inputs.source}
                  onChange={(e) =>
                    setInputs((p) => ({ ...p, source: e.target.value }))
                  }
                  className="w-full mt-2 p-2.5 bg-white/50 border rounded-lg outline-none"
                  autoFocus
                />
              )}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">
                客戶姓名
              </label>
              <input
                type="text"
                value={inputs.customerName}
                onChange={(e) =>
                  setInputs((p) => ({ ...p, customerName: e.target.value }))
                }
                className="w-full p-2.5 bg-white/50 border rounded-lg outline-none border-slate-200"
              />
            </div>
          </div>

          <div
            className={`${theme.light} rounded-xl border ${theme.border} overflow-hidden`}
          >
            <div
              className={`flex ${theme.bg} text-white text-xs font-semibold p-2 px-4 gap-2 opacity-90`}
            >
              <div className="flex-[2]">款式/品項</div>
              {productType === "sofa_mat" && (
                <div className="flex-[2]">部位</div>
              )}
              {productType !== "apron" && productType !== "sofa_mat" && (
                <>
                  <div className="flex-1">寬度</div>
                  <div className="flex-1">長度</div>
                </>
              )}
              {productType !== "sofa_mat" && (
                <div className="flex-1">件數</div>
              )}
              <div className="w-8"></div>
            </div>
            <div className="p-2 space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="animate-in slide-in-from-top-1 border-b border-dashed border-slate-200 pb-4 last:border-0 last:pb-0"
                >
                  <div
                    className={`flex gap-2 items-center ${
                      productType === "sofa_mat" ? "flex-wrap" : ""
                    }`}
                  >
                    {/* === 圍裙專用選擇器 === */}
                    {productType === "apron" && (
                      <div className="flex-[3] flex flex-col gap-2">
                        <div className="flex gap-2">
                          <select
                            className="w-1/3 p-2 border rounded text-sm bg-white"
                            value={item.apronCategory || ""}
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                "apronCategory",
                                e.target.value
                              )
                            }
                          >
                            <option value="" disabled>
                              分類
                            </option>
                            {APRON_CATEGORIES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              placeholder="搜尋款式..."
                              className="w-full p-2 border rounded text-sm mb-1"
                              value={apronSearch[item.id] || ""}
                              onChange={(e) =>
                                setApronSearch((p) => ({
                                  ...p,
                                  [item.id]: e.target.value,
                                }))
                              }
                            />
                            <select
                              value={item.style || ""}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "style",
                                  e.target.value
                                )
                              }
                              className="w-full p-2 border border-slate-200 rounded text-sm bg-white"
                            >
                              <option value="" disabled>
                                選擇款式
                              </option>
                              {apronStyles
                                .filter(
                                  (s) =>
                                    !item.apronCategory ||
                                    s.category === item.apronCategory
                                )
                                .filter(
                                  (s) =>
                                    !apronSearch[item.id] ||
                                    s.name.includes(apronSearch[item.id])
                                )
                                .map((s) => (
                                  <option key={s.id} value={s.name}>
                                    {s.name} (${s.cost})
                                  </option>
                                ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 其他產品選擇器 */}
                    {productType === "sofa_mat" && (
                      <select
                        value={item.style || ""}
                        onChange={(e) =>
                          handleItemChange(item.id, "style", e.target.value)
                        }
                        className="flex-[2] p-2 border border-orange-200 rounded-lg text-sm bg-white min-w-0"
                      >
                        <option value="" disabled>
                          選擇款式
                        </option>
                        {sofaStyles.map((s) => (
                          <option key={s.id} value={s.name}>
                            {s.name} (${s.cost})
                          </option>
                        ))}
                      </select>
                    )}
                    {productType === "sofa_mat" && (
                      <select
                        value={item.sofaArea || "free"}
                        onChange={(e) =>
                          handleItemChange(item.id, "sofaArea", e.target.value)
                        }
                        className="flex-[2] p-2 border border-orange-200 rounded-lg text-sm bg-white min-w-0"
                      >
                        {SOFA_AREA_TYPES.map((a) => (
                          <option key={a.value} value={a.value}>
                            {a.label}
                          </option>
                        ))}
                      </select>
                    )}
                    {productType === "carpet" && (
                      <select
                        value={item.style || ""}
                        onChange={(e) =>
                          handleItemChange(item.id, "style", e.target.value)
                        }
                        className="flex-[2] p-2 border border-purple-200 rounded-lg text-sm bg-white min-w-0"
                      >
                        <option value="" disabled>
                          選擇款式
                        </option>
                        {carpetStyles.map((s) => (
                          <option key={s.id} value={s.name}>
                            {s.name} (${s.cost})
                          </option>
                        ))}
                      </select>
                    )}
                    {productType === "edging_carpet" && (
                      <select
                        value={item.style || ""}
                        onChange={(e) =>
                          handleItemChange(item.id, "style", e.target.value)
                        }
                        className="flex-[2] p-2 border border-rose-200 rounded-lg text-sm bg-white min-w-0"
                      >
                        <option value="" disabled>
                          選擇款式
                        </option>
                        {edgingCarpetStyles.map((s) => (
                          <option key={s.id} value={s.name}>
                            {s.name} (${s.cost})
                          </option>
                        ))}
                      </select>
                    )}
                    {productType === "wall_sticker" && (
                      <select
                        value={item.style || ""}
                        onChange={(e) =>
                          handleItemChange(item.id, "style", e.target.value)
                        }
                        className="flex-[2] p-2 border border-cyan-200 rounded-lg text-sm bg-white min-w-0"
                      >
                        <option value="" disabled>
                          選擇款式
                        </option>
                        {wallStickerStyles.map((s) => (
                          <option key={s.id} value={s.name}>
                            {s.name} (${s.cost})
                          </option>
                        ))}
                      </select>
                    )}
                    {productType === "cushion" && (
                      <select
                        value={item.cushionType || "一般坐墊"}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleItemChange(item.id, "cushionType", val);
                          if (val === "特殊坐墊" || val === "特殊靠墊")
                            handleItemChange(item.id, "materialMult", 1.6);
                          else handleItemChange(item.id, "materialMult", 1);
                        }}
                        className="flex-[2] p-2 border border-emerald-200 rounded-lg text-sm bg-white min-w-0"
                      >
                        {CUSHION_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    )}

                    {productType !== "apron" && productType !== "sofa_mat" && (
                      <>
                        <input
                          type="number"
                          placeholder="寬"
                          value={item.width}
                          onChange={(e) =>
                            handleItemChange(item.id, "width", e.target.value)
                          }
                          className={`flex-1 p-2 border ${theme.border} rounded-lg text-center outline-none min-w-0`}
                        />
                        <input
                          type="number"
                          placeholder={productType === "sofa_mat" ? "深" : "長"}
                          value={item.length}
                          onChange={(e) =>
                            handleItemChange(item.id, "length", e.target.value)
                          }
                          className={`flex-1 p-2 border ${theme.border} rounded-lg text-center outline-none min-w-0`}
                        />
                      </>
                    )}

                    {productType === "sofa_mat" && (
                      <>
                        {/* 換行：讓款式與部位獨占第一列，避免文字被截斷 */}
                        <div className="w-full" />
                        <div className="flex-1 flex gap-2 items-end min-w-0">
                          {(() => {
                            const areaCfg = SOFA_AREA_TYPES.find(
                              (a) => a.value === (item.sofaArea || "free")
                            );
                            const needsHeight = areaCfg?.needsHeight;
                            const needsDepth = areaCfg?.needsDepth !== false;
                            const fields = [
                              { key: "width", label: "寬度" },
                              {
                                key: "length",
                                label: "深度",
                                off: !needsDepth,
                              },
                              { key: "height", label: "高度", off: !needsHeight },
                              { key: "quantity", label: "件數" },
                            ];
                            return fields.map((f) => (
                              <div
                                key={f.key}
                                className="flex-1 flex flex-col gap-0.5 min-w-0"
                              >
                                <span className="text-xs font-medium text-slate-600 px-1">
                                  {f.label}
                                </span>
                                <input
                                  type="number"
                                  disabled={f.off}
                                  title={
                                    f.off
                                      ? f.key === "length"
                                        ? "靠背區不需要填寫深度"
                                        : "僅扶手區需要填寫高度"
                                      : ""
                                  }
                                  value={f.off ? "" : item[f.key] ?? ""}
                                  onChange={(e) =>
                                    handleItemChange(
                                      item.id,
                                      f.key,
                                      e.target.value
                                    )
                                  }
                                  className={`w-full p-2 border ${theme.border} rounded-lg text-center outline-none min-w-0 disabled:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed`}
                                />
                              </div>
                            ));
                          })()}
                        </div>
                      </>
                    )}
                    {productType !== "sofa_mat" && (
                    <input
                        type="number"
                        placeholder="件"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(item.id, "quantity", e.target.value)
                        }
                        className={`flex-1 p-2 border ${theme.border} rounded-lg text-center outline-none min-w-0`}
                      />
                    )}
                    <button
                      onClick={() =>
                        setItems((p) =>
                          p.length > 1 ? p.filter((i) => i.id !== item.id) : p
                        )
                      }
                      className="w-8 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  {/* === 圍裙加工選項 === */}
                  {productType === "apron" && (
                    <div className="mt-2 pl-2 border-l-2 border-slate-200">
                      <div className="flex flex-col gap-2 text-sm mb-2">
                        <span className="font-bold text-slate-500">
                          LOGO加工:
                        </span>
                        <div className="flex flex-wrap gap-4">
                          {LOGO_PROCESSING_OPTIONS.map((opt) => (
                            <div
                              key={opt.value}
                              className="flex items-center gap-2"
                            >
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={
                                    !!(
                                      item.selectedLogos &&
                                      item.selectedLogos[opt.value] > 0
                                    )
                                  }
                                  onChange={(e) =>
                                    handleLogoChange(
                                      item.id,
                                      opt.value,
                                      e.target.checked
                                    )
                                  }
                                />
                                {opt.label}
                              </label>
                              {item.selectedLogos &&
                                item.selectedLogos[opt.value] > 0 && (
                                  <div className="flex items-center gap-1 bg-white border rounded px-1">
                                    <span>x</span>
                                    <input
                                      type="number"
                                      value={item.selectedLogos[opt.value]}
                                      onChange={(e) =>
                                        handleLogoCountChange(
                                          item.id,
                                          opt.value,
                                          e.target.value
                                        )
                                      }
                                      className="w-8 p-0.5 text-center outline-none"
                                      min="1"
                                    />
                                    <span className="text-xs text-slate-400">
                                      處
                                    </span>
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-4 items-center text-sm">
                        <span className="font-bold text-slate-500">
                          後加工:
                        </span>
                        <label className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={item.hasModification}
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                "hasModification",
                                e.target.checked
                              )
                            }
                          />{" "}
                          修改費(+$90)
                        </label>
                        <label className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={item.hasStrapChange}
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                "hasStrapChange",
                                e.target.checked
                              )
                            }
                          />{" "}
                          改背帶(+$150)
                        </label>
                        <div className="ml-auto flex items-center gap-2">
                          <span>版費數量:</span>
                          <input
                            type="number"
                            value={item.plateCount}
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                "plateCount",
                                e.target.value
                              )
                            }
                            className="w-12 p-1 border rounded text-center"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 其他產品詳細選項 */}
                  {productType === "cushion" &&
                    !item.cushionType?.includes("抱枕") && (
                      <>
                        <div className="flex gap-2 items-center mt-2 pl-2">
                          <div className="flex-1 flex items-center gap-1">
                            <span className="text-xs text-slate-400">厚度</span>
                            <select
                              value={item.thickness}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "thickness",
                                  e.target.value
                                )
                              }
                              className="w-full p-1 border rounded text-sm bg-white"
                            >
                              {cushionThicknesses
                                .filter((t) => t.cm !== 0)
                                .map((t) => (
                                  <option key={t.id} value={t.cm}>
                                    {t.cm}cm
                                  </option>
                                ))}
                            </select>
                          </div>
                          {!item.cushionType?.includes("單布套") && (
                            <div className="flex-1 flex items-center gap-1">
                              <span className="text-xs text-slate-400">
                                密度
                              </span>
                              <select
                                value={item.density}
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "density",
                                    e.target.value
                                  )
                                }
                                className="w-full p-1 border rounded text-sm bg-white"
                              >
                                {CUSHION_DENSITIES.map((d) => (
                                  <option key={d} value={d}>
                                    {d}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div className="flex-1 flex items-center gap-1">
                            <span className="text-xs text-slate-400">加成</span>
                            <input
                              type="number"
                              step="0.1"
                              value={item.materialMult}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "materialMult",
                                  e.target.value
                                )
                              }
                              className="w-full p-1 border rounded text-sm text-center"
                            />
                          </div>
                          <div className="flex-1 flex items-center gap-1">
                            <span className="text-xs text-slate-400">加工</span>
                            <input
                              type="number"
                              value={item.processFee}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "processFee",
                                  e.target.value
                                )
                              }
                              placeholder={item.calculatedProcessFee}
                              className="w-full p-1 border rounded text-sm text-center"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 items-center mt-2 pl-2 text-sm text-slate-600 flex-wrap">
                          {item.cushionType?.includes("靠墊") && (
                            <select
                              value={item.cushionOption || "A"}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "cushionOption",
                                  e.target.value
                                )
                              }
                              className="p-1 border rounded bg-white mr-1"
                            >
                              {CUSHION_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          )}
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={item.isIrregular}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "isIrregular",
                                  e.target.checked
                                )
                              }
                            />{" "}
                            異形
                          </label>
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={item.isRounded}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "isRounded",
                                  e.target.checked
                                )
                              }
                            />{" "}
                            圓角
                          </label>
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={item.isDoubleSided}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "isDoubleSided",
                                  e.target.checked
                                )
                              }
                            />{" "}
                            雙面
                          </label>
                          <div className="flex items-center gap-1 ml-auto border rounded px-2 bg-white">
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={item.hasStraps}
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "hasStraps",
                                    e.target.checked
                                  )
                                }
                              />{" "}
                              綁帶
                            </label>
                            {item.hasStraps && (
                              <input
                                type="number"
                                value={item.strapCount}
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "strapCount",
                                    e.target.value
                                  )
                                }
                                className="w-8 text-center border-l pl-1"
                              />
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  {productType === "wall_sticker" && (
                    <div className="flex gap-4 items-center mt-2 pl-2 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">厚度</span>
                        <select
                          value={item.wsThickness}
                          onChange={(e) =>
                            handleItemChange(
                              item.id,
                              "wsThickness",
                              e.target.value
                            )
                          }
                          className="p-1 border border-slate-200 rounded text-sm bg-white"
                        >
                          <option value="3">3cm</option>
                          <option value="5">5cm</option>
                          <option value="8">8cm</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">材質</span>
                        <select
                          value={item.wsFireType}
                          onChange={(e) =>
                            handleItemChange(
                              item.id,
                              "wsFireType",
                              e.target.value
                            )
                          }
                          className="p-1 border border-slate-200 rounded text-sm bg-white"
                        >
                          <option value="normal">密集板</option>
                          <option value="fire">耐燃</option>
                        </select>
                      </div>
                    </div>
                  )}
                  {productType === "dust_mite" && (
                    <div className="flex gap-4 items-center mt-2 pl-2 text-sm text-slate-600">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={item.hasCuttingFee}
                          onChange={(e) =>
                            handleItemChange(
                              item.id,
                              "hasCuttingFee",
                              e.target.checked
                            )
                          }
                        />{" "}
                        裁切費
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={item.hasPlateFee}
                          onChange={(e) =>
                            handleItemChange(
                              item.id,
                              "hasPlateFee",
                              e.target.checked
                            )
                          }
                        />{" "}
                        版費
                      </label>
                    </div>
                  )}
                  {(productType === "edging_carpet" ||
                    productType === "carpet") && (
                    <div className="flex gap-4 items-center mt-2 pl-2 text-sm text-slate-600">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={item.hasPlateFee}
                          onChange={(e) =>
                            handleItemChange(
                              item.id,
                              "hasPlateFee",
                              e.target.checked
                            )
                          }
                        />{" "}
                        排版費
                      </label>
                      <div className="flex items-center gap-1 ml-auto">
                        <span className="text-xs text-slate-400">加工費:</span>
                        <input
                          type="number"
                          value={item.processFee}
                          onChange={(e) =>
                            handleItemChange(
                              item.id,
                              "processFee",
                              e.target.value
                            )
                          }
                          className="w-16 p-1 border rounded text-center"
                        />
                      </div>
                    </div>
                  )}
                  {item.warningMsg && (
                    <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {item.warningMsg}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="p-2 border-t border-white/50 flex justify-center">
              <button
                onClick={addItemRow}
                className={`flex items-center gap-1 ${theme.text} hover:bg-white px-3 py-1.5 rounded-full text-sm font-medium transition-colors`}
              >
                <Plus size={16} /> 新增一列
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            {editingHistoryId ? (
              <button
                onClick={handleCancelEdit}
                className="flex-1 py-3 border border-red-300 text-red-600 bg-red-50 rounded-xl hover:bg-red-100 flex items-center justify-center gap-2 font-medium"
              >
                <XCircle size={18} /> 取消編輯
              </button>
            ) : (
              <button
                onClick={clearForm}
                className="flex-1 py-3 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 flex items-center justify-center gap-2 font-medium"
              >
                <RefreshCw size={18} /> 重置
              </button>
            )}
            <button
              onClick={handleCopyAndSave}
              disabled={!validateForm()}
              className={`flex-[2] py-3 rounded-xl text-white flex items-center justify-center gap-2 font-bold shadow-md transition-all ${
                validateForm()
                  ? editingHistoryId
                    ? "bg-amber-600 hover:bg-amber-700"
                    : `${theme.bg} hover:opacity-90`
                  : "bg-slate-300 cursor-not-allowed"
              }`}
            >
              <Copy size={20} /> {editingHistoryId ? "更新紀錄" : "複製並儲存"}
            </button>
          </div>
        </div>
      </div>

      {/* 右側資訊欄 */}
      <div className="space-y-4">
        {relatedRecords.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-2">
            <div className="bg-slate-100 p-3 px-4 border-b border-slate-200 flex items-center gap-2 text-slate-700 font-bold text-sm">
              <Search size={16} /> 相關歷史紀錄
            </div>
            <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
              {relatedRecords.map((r) => (
                <div
                  key={r.id}
                  className="p-3 hover:bg-slate-50 text-xs cursor-pointer group"
                  onClick={() => loadHistoryRecord(r)}
                >
                  <div className="flex justify-between font-bold text-slate-800">
                    <span>{r.customerName}</span>
                    <span className="text-slate-400 font-normal">{r.date}</span>
                  </div>
                  <div className="mt-1 space-y-1">
                    {(r.items || []).map((item, idx) => {
                      let displayName = item.styleName || item.cushionType;
                      if (r.productType === "dust_mite") displayName = "除塵墊";

                      const detailStr = getItemDetails(item, r.productType);

                      return (
                        <div
                          key={idx}
                          className="text-slate-500 flex flex-wrap gap-1 items-center"
                        >
                          <span className="font-bold text-slate-600">
                            {displayName}
                          </span>
                          {detailStr && <span>{detailStr}</span>}
                          <span className="text-slate-400">
                            {item.width ? `${item.width}x${item.length}` : ""}(
                            {item.quantity}件)
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-between mt-2 pt-1 border-t border-slate-100">
                    <span className="font-bold text-slate-700">
                      ${r.totalAmount.toLocaleString()}
                    </span>
                    <span className="text-blue-600 opacity-0 group-hover:opacity-100">
                      載入
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden sticky top-6">
          <div
            className={`bg-gradient-to-br ${
              productType === "dust_mite"
                ? "from-blue-500 to-indigo-600"
                : productType === "sofa_mat"
                ? "from-orange-500 to-red-600"
                : productType === "carpet"
                ? "from-purple-500 to-indigo-600"
                : productType === "edging_carpet"
                ? "from-rose-500 to-pink-600"
                : productType === "wall_sticker"
                ? "from-cyan-500 to-blue-600"
                : productType === "apron"
                ? "from-yellow-600 to-orange-700"
                : "from-emerald-500 to-teal-600"
            } text-white p-6 relative`}
          >
            <h3 className="text-white/90 text-sm font-medium mb-1">
              總報價預覽
            </h3>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-bold tracking-tight">
                ${calculationResult.grandTotal.toLocaleString()}
              </span>
              {calculationResult.isTaxExcluded && (
                <span className="text-xs bg-white text-slate-800 px-1.5 py-0.5 rounded font-bold">
                  未稅
                </span>
              )}
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-sm bg-white/20 w-fit px-3 py-1 rounded-full">
                <TrendingDown size={16} />{" "}
                <span>{calculationResult.discountLabel}</span>
              </div>
              {calculationResult.nextTierDiff > 0 && (
                <div className="text-xs text-white/90 bg-black/10 px-3 py-1.5 rounded-lg flex items-center gap-1">
                  <Info size={14} /> 還差 $
                  {calculationResult.nextTierDiff.toLocaleString()} 享下一層折扣
                </div>
              )}

              {/* ★★★ 修改重點 1：允許 dust_mite 顯示警告 ★★★ */}
              {(productType === "carpet" ||
                productType === "edging_carpet" ||
                productType === "dust_mite") &&
                calculationResult.items.map(
                  (i, idx) =>
                    i.isValid &&
                    i.warningMsg && (
                      <div
                        key={idx}
                        className="text-xs text-white/90 bg-white/20 px-3 py-1.5 rounded-lg flex items-start gap-1"
                      >
                        <AlertCircle size={14} className="mt-0.5 shrink-0" />
                        <span>
                          #{idx + 1}: {i.warningMsg}
                        </span>
                      </div>
                    )
                )}

              {userRole === "admin" && (
                <div className="mt-2 pt-2 border-t border-white/20 text-[10px] text-white/80 space-y-1">
                  <div className="font-bold mb-1 opacity-100 flex items-center gap-1">
                    <Lock size={10} /> 管理員除錯資訊
                  </div>
                  {productType === "apron" ? (
                    <div>圍裙無詳細RMB成本紀錄</div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span>RMB 商品:</span>
                        <span>
                          ¥
                          {(
                            calculationResult.debugData?.totalRmbCost || 0
                          ).toFixed(1)}
                        </span>
                      </div>
                      {(calculationResult.debugData?.totalRmbPalletFee || 0) > 0 && (
                        <div className="flex justify-between">
                          <span>RMB 貨板費:</span>
                          <span>
                            ¥
                            {(
                              calculationResult.debugData?.totalRmbPalletFee || 0
                            ).toFixed(1)}
                          </span>
                        </div>
                      )}
                      {(calculationResult.debugData?.totalRmbExtraCost || 0) >
                        0 && (
                        <div className="flex justify-between">
                          <span>RMB 固定加價:</span>
                          <span>
                            ¥
                            {(
                              calculationResult.debugData?.totalRmbExtraCost || 0
                            ).toFixed(1)}
                          </span>
                        </div>
                      )}
                      {(calculationResult.debugData?.totalRmbLabelFee || 0) >
                        0 && (
                        <div className="flex justify-between">
                          <span>RMB 標籤費:</span>
                          <span>
                            ¥
                            {(
                              calculationResult.debugData?.totalRmbLabelFee || 0
                            ).toFixed(1)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>RMB 運費:</span>
                        <span>
                          ¥
                          {(
                            calculationResult.debugData?.totalRmbFreight || 0
                          ).toFixed(1)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>RMB 耗損:</span>
                        <span>
                          ¥
                          {(
                            calculationResult.debugData?.totalRmbWear || 0
                          ).toFixed(1)}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-white/10 pt-1 font-bold">
                        <span>RMB 總計:</span>
                        <span>
                          ¥
                          {(
                            (calculationResult.debugData?.totalRmbCost || 0) +
                            (calculationResult.debugData?.totalRmbPalletFee ||
                              0) +
                            (calculationResult.debugData?.totalRmbExtraCost ||
                              0) +
                            (calculationResult.debugData?.totalRmbLabelFee ||
                              0) +
                            (calculationResult.debugData?.totalRmbFreight ||
                              0) +
                            (calculationResult.debugData?.totalRmbWear || 0)
                          ).toFixed(1)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
            {calculationResult.items.filter((i) => i.isValid).length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">
                請輸入規格以顯示報價
              </div>
            ) : (
              calculationResult.items.map((item, idx) =>
                !item.isValid ? null : (
                  <div key={item.id} className="p-4 hover:bg-slate-50">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-bold text-slate-700">
                        {/* ★★★ 修改重點 2：列表品項直接顯示費用與警告 ★★★ */}
                        {idx + 1}.{" "}
                        {productType === "dust_mite"
                          ? "除塵墊"
                          : item.styleName || item.cushionType}{" "}
                        {productType === "dust_mite" &&
                          item.cuttingFee > 0 && (
                            <span className="text-slate-500 font-normal">
                              {" "}
                              [裁切費${item.cuttingFee}]
                            </span>
                          )}
                        {productType === "dust_mite" && item.plateFee > 0 && (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            [版費${item.plateFee}]
                          </span>
                        )}{" "}
                        {item.width ? `${item.width}x${item.length}` : ""}
                        {/* 顯示紅色警告 */}
                        {item.warningMsg && (
                          <span className="text-red-500 font-bold ml-1">
                            {item.warningMsg}
                          </span>
                        )}
                      </span>
                      <span className={`font-bold ${theme.text}`}>
                        ${item.finalPrice.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex flex-col text-xs text-slate-500">
                      <div className="flex justify-between">
                        <span>數量: {item.quantity}</span>
                        <span>
                          {productType === "apron"
                            ? ""
                            : `原價 $${item.twdBaseSellingPrice}`}
                        </span>
                      </div>
                      {productType === "apron" && (
                        <div className="mt-1 text-slate-400">
                          {item.selectedLogos &&
                            Object.entries(item.selectedLogos)
                              .map(([k, v]) =>
                                v > 0
                                  ? `${
                                      LOGO_PROCESSING_OPTIONS.find(
                                        (o) => o.value === k
                                      )?.label
                                    }x${v} `
                                  : ""
                              )
                              .join("")}
                          {item.hasModification && "修改 "}
                          {item.hasStrapChange && "改背帶"}
                        </div>
                      )}
                    </div>
                  </div>
                )
              )
            )}
          </div>
          {calculationResult.hasValidItems && (
            <div className="p-4 bg-slate-50 border-t border-slate-200 space-y-2">
              <button
                onClick={() => {
                  const t = document.createElement("textarea");
                  t.value = generateQuotationText();
                  document.body.appendChild(t);
                  t.select();
                  document.execCommand("copy");
                  document.body.removeChild(t);
                  showToast("已複製文字！");
                }}
                className={`w-full bg-white border ${theme.border} ${theme.text} py-2 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center justify-center gap-2`}
              >
                <Copy size={16} /> 僅複製文字
              </button>
              <button
                onClick={handleCopyItemizedPrices}
                className={`w-full bg-white border ${theme.border} ${theme.text} py-2 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center justify-center gap-2`}
              >
                <List size={16} /> 複製每個品項價格
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Calculator;